import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  // Set whenever any axios call comes back 401, so pages can pop the dialog.
  const [authPromptCounter, setAuthPromptCounter] = useState(0);

  // Restore session from localStorage on first mount.
  useEffect(() => {
    const storedPassword = localStorage.getItem("adminPassword");
    if (storedPassword) {
      setPassword(storedPassword);
      setIsAuthenticated(true);
    }
  }, []);

  // Global axios interceptors:
  //   - Request: auto-attach X-Admin-Password header on every API call
  //   - Response: clear auth + bump prompt counter on 401 so pages can react
  // Skips /api/share/ and /api/auth/verify (public endpoints).
  useEffect(() => {
    const reqId = axios.interceptors.request.use((config) => {
      const url = config.url || "";
      const isPublic = url.includes("/api/share/") || url.includes("/api/auth/verify");
      const pw = password || localStorage.getItem("adminPassword");
      if (pw && !isPublic && !config.headers["X-Admin-Password"]) {
        config.headers["X-Admin-Password"] = pw;
      }
      return config;
    });

    const respId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const url = error.config?.url || "";
          const isPublic = url.includes("/api/share/") || url.includes("/api/auth/verify");
          if (!isPublic) {
            setIsAuthenticated(false);
            setPassword("");
            localStorage.removeItem("adminPassword");
            setAuthPromptCounter((c) => c + 1);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(respId);
    };
  }, [password]);

  const login = async (inputPassword, opts = {}) => {
    try {
      const response = await axios.post(`${API}/auth/verify`, {
        password: inputPassword,
        website: opts.website || "",
        turnstile_token: opts.turnstileToken || "",
      });

      if (response.data.valid) {
        setPassword(inputPassword);
        setIsAuthenticated(true);
        localStorage.setItem("adminPassword", inputPassword);
        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || "Invalid password"
      };
    }
  };

  const logout = () => {
    setPassword("");
    setIsAuthenticated(false);
    localStorage.removeItem("adminPassword");
  };

  // Get headers with password for authenticated requests
  const getAuthHeaders = () => {
    if (password) {
      return { "X-Admin-Password": password };
    }
    return {};
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      login,
      logout,
      getAuthHeaders,
      password,
      authPromptCounter,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
